import {
    ChartConfig,
    ChartContainer,
    ChartLegend,
    ChartTooltip,
    ChartTooltipContent
} from "../../../../../components/general/chart.tsx";
import {Cell, Pie, PieChart, Sector} from "recharts";
import {useEffect, useState} from "react";
import {deserializeCashflowPageState} from "../CashflowPage/CashflowPageState.ts";
import {Link} from "react-router-dom";
import { decimalToLocalizedString } from "../../../../../lib/Money.ts";
import {breakdownToChartData, capIfLong, PieChartCategory} from "../../../../../lib/utils.ts";
import {getAccentChartColor, getActiveAccentColors, PIE_DEFAULT_FILL} from "../../../../../lib/accentPalette";

type RevenueCategory = PieChartCategory & { color: string };

function renderActiveSlice(props: any) {
    const {cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill} = props;
    return (
        <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius + 6}
            outerRadius={outerRadius + 6}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
        />
    );
}

type RevenueCategoriesPieChartProps = {
    data: {
        expenses: {
            [key: string]: {
                percentage: number;
                amount: number;
                icon?: string | null;
            };
        };
        revenues: {
            [key: string]: {
                percentage: number;
                amount: number;
                icon?: string | null;
            };
        };
    } | undefined;
    className?: string;
    colorMode?: "icon" | "palette";
};

export default function RevenueCategoriesPieChart({data, className, colorMode = "palette"}: RevenueCategoriesPieChartProps) {
    const [chartData, setChartData] = useState<RevenueCategory[]>([]);
    const [config, setConfig] = useState<ChartConfig>({});
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    useEffect(() => {
        if (!data || !data.revenues) return;

        const accentColors = getActiveAccentColors();
        const processedData: RevenueCategory[] = breakdownToChartData(data.revenues).map((category, index) => ({
            ...category,
            color: colorMode === "icon" ? getAccentChartColor(category.icon) : accentColors[index % accentColors.length],
        }));
        setChartData(processedData);

        const newConfig: ChartConfig = {};
        processedData.forEach((category) => {
            newConfig[category.name] = {
                label: category.name,
                color: category.color,
            };
        });
        setConfig(newConfig);
    }, [data, colorMode]);

    if (!data || !data.revenues || Object.keys(data.revenues).length === 0) {
        return <div className="p-8 rounded-lg bg-white border-2 border-gray-50 flex-1 text-center">Geen gegevens beschikbaar</div>;
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 30;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="gray" fontSize="12" textAnchor="middle" dominantBaseline="central">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    const drilldownUrl = '/cash-flow?' + deserializeCashflowPageState({
        flow: 'inflow', month: new Date().toISOString()
    });

    return (
        <div className={`p-8 rounded-lg bg-white border-2 border-gray-50 flex-1 ${className || ""}`}>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-gray-900">Inkomsten breakdown</h3>
                <Link to={drilldownUrl} className="text-blue-400 hover:text-blue-500 text-sm flex items-center gap-2">
                    BREAKDOWN →
                </Link>
            </div>

            <ChartContainer config={config} className="h-[300px] w-full">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="40%"
                        cy="50%"
                        labelLine={true}
                        label={renderCustomizedLabel}
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        fill={PIE_DEFAULT_FILL}
                        dataKey="amount"
                        nameKey="name"
                        activeIndex={activeIndex >= 0 ? activeIndex : undefined}
                        activeShape={renderActiveSlice}
                        onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(-1)}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color}/>
                        ))}
                    </Pie>
                    <ChartTooltip
                        content={
                            <ChartTooltipContent
                                formatter={(value, name) => [
                                    `€ ${decimalToLocalizedString(value as number)} - `,
                                    name as string,
                                ]}
                            />
                        }
                    />
                    <ChartLegend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        content={({payload}) => (
                            <div className="flex flex-col space-y-2">
                                {payload?.map((entry, index) => {
                                    const dataItem = chartData.find(item => item.name === entry.value);

                                    return (
                                        <div key={`item-${index}`} className="flex items-center gap-1.5">
                                            <div
                                                className="h-2 w-2 shrink-0 rounded-[2px]"
                                                style={{backgroundColor: entry.color}}
                                            />
                                            <span>{capIfLong(entry.value as string)}</span>
                                            {dataItem && (
                                                <span className="ml-1 text-xs text-gray-500">
                                                    (€ {decimalToLocalizedString(dataItem.amount)})
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    />
                </PieChart>
            </ChartContainer>
        </div>
    );
}
